'use strict';

/**
 * Pict-Provider-BlueprintAPI
 * --------------------------
 * Browser-side client for blueprints, polymorphic over the foundational entity.
 *
 * A blueprint can be named two ways, and until now the client only knew one of them:
 *
 *   BY ITS HOST     a Vision / Module / Feature / WorkItem that owns one
 *                   GET /1.0/<EntityKey>/<id>/Blueprint
 *   BY ITSELF       the Blueprint record, which since WI #340 is first-class: its own
 *                   IDBlueprint, its own BlueprintNumber, its own page
 *                   GET /1.0/Blueprint/<IDBlueprint>/Items
 *
 * Every client path spoke only the first. Four separate places defaulted a missing key to the
 * literal string 'WorkItem' (PictView-Blueprint, BlueprintShareUI twice, the route handler), and
 * handing any of them a blueprint's own id produced /1.0/Blueprint/<id>/Blueprint -- which matches
 * the generic wildcard route, resolves against the Connections manifest, finds no 'Blueprint' key
 * there, and 404s. So a blueprint's own page could not read itself, and the by-id print and share
 * routes the server had already built had no caller at all.
 *
 * THE SUBJECT is what this provider adds. A subject is "which blueprint do you mean", in either
 * dialect, and every method takes one:
 *
 *   subjectForEntity('Feature', 1)   ->  { Kind: 'entity', EntityKey: 'Feature', ID: 1 }
 *   subjectForRecord(117)            ->  { Kind: 'record', ID: 117 }
 *   subjectFromPayload(pPayload)     ->  a RECORD subject when the payload carries IDBlueprint
 *
 * A caller that opened by host can hand the payload back through subjectFromPayload and act by
 * record from then on, which is what pinning needs: pins hang off IDBlueprint, so they were never
 * addressable from the host dialect without a round trip nobody was making.
 *
 * WHAT THIS DOES NOT DO: it does not decide what a blueprint CONTAINS. The server already does
 * that correctly for both dialects -- buildBlueprintForRecord dispatches on OwnerType and lands on
 * the same builders the host routes use, so the two reads return the same payload shape. This
 * provider resolves addresses and normalizes the identity fields; it invents nothing.
 *
 * It subclasses EntityAPI so plain Blueprint CRUD (the Blueprints list) keeps working, and is
 * registered BEFORE the EntityDescriptors loop in Pict-Application-Plansheet.js, whose
 * `if (this.pict.providers[Key + 'API']) { continue; }` guard is the intended way to supply a
 * bespoke provider for an entity that would otherwise get the generic one.
 */

const libPictProviderEntityAPI = require('./Pict-Provider-EntityAPI.js');
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'BlueprintAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/',
	EntityName: 'Blueprint',
	PluralRoute: 'Blueprints',
	IDField: 'IDBlueprint'
};

// The entities that own a blueprint. This is the ONLY list of them on the client side; the four
// `|| 'WorkItem'` fallbacks scattered through the views are what it replaces. A key absent here is
// not blueprintable, and asking for its blueprint is a caller error rather than a silent WorkItem.
const _HOST_ENTITIES = { WorkItem: true, Vision: true, Module: true, Feature: true };

// What the back-link calls the thing it returns to. Saying the wrong noun is how somebody ends up
// on the wrong record and believes the page.
const _HOST_NOUNS = { WorkItem: 'work item', Vision: 'vision', Module: 'module', Feature: 'feature' };

function _pad(pNumber) { return String(pNumber).padStart(3, '0'); }

class PictProviderBlueprintAPI extends libPictProviderEntityAPI
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultProviderConfiguration, pOptions || {}), pServiceHash);
	}

	// ---- subjects --------------------------------------------------------------

	/** A blueprint named by the entity that owns it. */
	subjectForEntity(pEntityKey, pID)
	{
		return { Kind: 'entity', EntityKey: String(pEntityKey || ''), ID: parseInt(pID, 10) || 0 };
	}

	/** A blueprint named by its own record. */
	subjectForRecord(pIDBlueprint)
	{
		return { Kind: 'record', EntityKey: 'Blueprint', ID: parseInt(pIDBlueprint, 10) || 0 };
	}

	/**
	 * The record subject for a payload already read. Pins, cover and the by-id share family all
	 * need IDBlueprint, which a host-addressed read is what tells you.
	 *
	 * Returns null when the payload carries no record yet: an entity whose blueprint has never been
	 * read has no Blueprint row, and the server reports IDBlueprint 0 rather than inventing one.
	 */
	subjectFromPayload(pPayload)
	{
		let tmpID = pPayload && parseInt(pPayload.IDBlueprint, 10);
		return tmpID ? this.subjectForRecord(tmpID) : null;
	}

	/** Is this entity key one that owns a blueprint? */
	isHostEntity(pEntityKey) { return !!_HOST_ENTITIES[pEntityKey]; }

	/** Every blueprint-owning entity key, for callers that need to enumerate rather than test. */
	hostEntities() { return Object.keys(_HOST_ENTITIES); }

	/** What to call the record a blueprint describes, for back-links and prose. */
	ownerNoun(pSubject)
	{
		if (!pSubject || pSubject.Kind === 'record') { return 'blueprint'; }
		return _HOST_NOUNS[pSubject.EntityKey] || 'record';
	}

	_valid(pSubject)
	{
		if (!pSubject || !pSubject.ID) { return false; }
		return (pSubject.Kind === 'record') || this.isHostEntity(pSubject.EntityKey);
	}

	// ---- addressing ------------------------------------------------------------

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	/**
	 * The base path for a subject, and the one place the two dialects differ.
	 *
	 *   record:  Blueprint/117            -> suffixes are /Items /Print /Share /Invite /Item
	 *   entity:  Feature/1/Blueprint      -> suffixes are (none) /Print /Share /Invite
	 *
	 * The read suffix differs between them, which is why _readPath exists separately rather than
	 * every caller remembering that a record reads /Items and a host reads nothing.
	 */
	_base(pSubject)
	{
		if (pSubject.Kind === 'record') { return 'Blueprint/' + encodeURIComponent(pSubject.ID); }
		return encodeURIComponent(pSubject.EntityKey) + '/' + encodeURIComponent(pSubject.ID) + '/Blueprint';
	}

	_readPath(pSubject)
	{
		return (pSubject.Kind === 'record') ? (this._base(pSubject) + '/Items') : this._base(pSubject);
	}

	/** The URL a caller can hand to a link or a new tab, rather than fetch itself. */
	printURL(pSubject, pSheet)
	{
		if (!this._valid(pSubject)) { return ''; }
		let tmpSheet = pSheet ? ('?sheet=' + encodeURIComponent(pSheet)) : '';
		return this._url(this._base(pSubject) + '/Print' + tmpSheet);
	}

	/** The resolution preflight for a size: which images would print too soft, without rendering a PDF. */
	checkURL(pSubject, pSheet, pReading)
	{
		if (!this._valid(pSubject)) { return ''; }
		return this._url(this._base(pSubject) + '/Print/Check?sheet=' + encodeURIComponent(pSheet || 'Letter')
			+ '&reading=' + encodeURIComponent(pReading || 'Desk'));
	}

	// ---- reading ---------------------------------------------------------------

	/**
	 * Read a blueprint in either dialect. Both return the same payload shape (the by-record route
	 * dispatches through buildBlueprintForRecord onto the very same builders the host routes use),
	 * so a caller never has to know which one answered.
	 *
	 * Resolves null rather than rejecting: a blueprint that cannot be read should render as an
	 * empty sheet, not tear down the page around it.
	 *
	 * NOT NAMED read(). This class extends the generic EntityAPI, whose read(pID) fetches one record
	 * by id, and naming this one read() overrode that with an incompatible contract. Handed a numeric
	 * id it fell at the _valid() guard and resolved null WITHOUT ISSUING A REQUEST, so the detail
	 * page's read-by-id silently did nothing for blueprints alone. That read-by-id is what lets a
	 * record outside the capped list page open at all (WI #329), so on a tenant with more blueprints
	 * than one page every blueprint past the first 250 opened as "Blueprint not found" -- the record
	 * was fine, nothing had ever asked the server for it. A small tenant never sees it, because every
	 * blueprint is on page one and the list cache answers instead.
	 */
	readSubject(pSubject)
	{
		if (!this._valid(pSubject)) { return Promise.resolve(null); }
		return libFrameworkClient.getJSON(this.fable, this._url(this._readPath(pSubject)))
			.then((pBody) => this.normalize(pBody, pSubject))
			.catch(() => null);
	}

	/**
	 * Give every blueprint a mark it can name itself by.
	 *
	 * The three server builders emit different identity fields by owner type, and BlueprintRender
	 * reads them in one cascade: Designator, else WorkItemNumber as WI-###, else the literal
	 * 'WI #' + IDWorkItem. A work item has a WorkItemNumber and a Vision/Module/Feature has a
	 * Designator, so both land correctly -- but a blueprint addressed as itself has NEITHER, and
	 * falls all the way through to "WI #0". A first-class blueprint titling itself as work item
	 * zero is the same category error as the addressing this provider exists to fix, so the mark is
	 * filled in here, once, from BlueprintNumber and the Blueprint descriptor's own BP designator.
	 */
	normalize(pPayload, pSubject)
	{
		if (!pPayload || typeof pPayload !== 'object') { return null; }
		if (!pPayload.Designator)
		{
			if (pPayload.WorkItemNumber) { pPayload.Designator = 'WI-' + _pad(pPayload.WorkItemNumber); }
			else if (pPayload.BlueprintNumber) { pPayload.Designator = 'BP-' + _pad(pPayload.BlueprintNumber); }
		}
		// Remember how this blueprint was reached, so a view can re-address it without re-deriving.
		pPayload.Subject = this.subjectFromPayload(pPayload) || pSubject || null;
		return pPayload;
	}

	/** The pin candidates of one type. Curation is by record, so this needs IDBlueprint. */
	candidates(pSubject, pType)
	{
		if (!pSubject || pSubject.Kind !== 'record' || !pSubject.ID) { return Promise.resolve([]); }
		return libFrameworkClient.getJSON(this.fable,
			this._url(this._base(pSubject) + '/Candidates/' + encodeURIComponent(pType)))
			.then((pBody) => Array.isArray(pBody) ? pBody : ((pBody && pBody.Records) || []))
			.catch(() => []);
	}

	// ---- curation --------------------------------------------------------------
	//
	// A WORK ITEM'S PINS ARE NOT KEYED THE SAME WAY, and this is the one asymmetry the provider must
	// keep rather than smooth over. POST /1.0/WorkItem/:id/Blueprint/Item writes BlueprintItem with
	// IDWorkItem set and no IDBlueprint (Blueprint-Endpoint.js:1219), and the work-item gather reads
	// its pins back by IDWorkItem (:280). POST /1.0/Blueprint/:id/Item writes the mirror image:
	// IDBlueprint set, IDWorkItem 0 (:1394-1396). Send a work item's pin down the record route and it
	// is written with IDWorkItem 0, so the work item's own blueprint never sees it again -- the pin
	// appears to succeed and silently vanishes.
	//
	// Every pin ever made on a work item is stored the first way, so this is not a migration waiting
	// to happen; it is how work-item pins are addressed. curationSubject() is the single place that
	// knows, so no caller has to.

	/**
	 * Which subject to curate against. Work items curate by their own id; everything else curates by
	 * the blueprint record, which a host-addressed read reveals.
	 */
	curationSubject(pPayload, pSubject)
	{
		if (pSubject && pSubject.Kind === 'entity' && pSubject.EntityKey === 'WorkItem') { return pSubject; }
		return this.subjectFromPayload(pPayload) || ((pSubject && pSubject.Kind === 'record') ? pSubject : null);
	}

	_itemBase(pSubject)
	{
		if (pSubject && pSubject.Kind === 'entity' && pSubject.EntityKey === 'WorkItem')
		{
			return 'WorkItem/' + encodeURIComponent(pSubject.ID) + '/Blueprint/Item';
		}
		if (pSubject && pSubject.Kind === 'record' && pSubject.ID) { return 'Blueprint/' + encodeURIComponent(pSubject.ID) + '/Item'; }
		return '';
	}

	pin(pSubject, pItemType, pIDItem)
	{
		let tmpBase = this._itemBase(pSubject);
		if (!tmpBase) { return Promise.reject(new Error('BlueprintAPI: pinning needs a work item or a blueprint record. Read the blueprint first, then use curationSubject().')); }
		return libFrameworkClient.postJSON(this.fable, this._url(tmpBase),
			{ ItemType: pItemType, IDItem: parseInt(pIDItem, 10) || 0 });
	}

	unpin(pSubject, pIDBlueprintItem)
	{
		let tmpBase = this._itemBase(pSubject);
		if (!tmpBase) { return Promise.reject(new Error('BlueprintAPI: unpinning needs a work item or a blueprint record.')); }
		return libFrameworkClient.delJSON(this.fable,
			this._url(tmpBase + '/' + (parseInt(pIDBlueprintItem, 10) || 0)));
	}

	// ---- sharing ---------------------------------------------------------------
	// Both dialects have a full share family on the server. The host one was the only one wired,
	// and asking it about a blueprint record answered a cheerful lie: the generic
	// /1.0/:EntityType/:id/Blueprint/Share route resolves EntityType through the Connections
	// manifest, which has no 'Blueprint' key, so it returned 200 { Exists: false } for a blueprint
	// that may well have been shared. Addressing by record removes the question.

	/**
	 * Take a version of a sheet: freeze it as it is now.
	 *
	 * KEYED ON THE BLUEPRINT ROW, not the subject, because a version belongs to the sheet itself rather
	 * than to whichever entity happens to host it. A Feature's blueprint and the Feature are two
	 * different ids, and versioning the wrong one would file the snapshot where nothing looks for it.
	 */
	takeVersion(pIDBlueprint)
	{
		let tmpID = Number(pIDBlueprint) || 0;
		if (!tmpID) { return Promise.reject(new Error('BlueprintAPI: no sheet to version.')); }
		return libFrameworkClient.postJSON(this.fable, this._url('Blueprint/' + tmpID + '/Version'), {});
	}

	/** Every version of a sheet, newest first. */
	versions(pIDBlueprint)
	{
		let tmpID = Number(pIDBlueprint) || 0;
		if (!tmpID) { return Promise.resolve([]); }
		return libFrameworkClient.getJSON(this.fable, this._url('Blueprint/' + tmpID + '/Versions'))
			.then((pBody) => ((pBody && Array.isArray(pBody.Versions)) ? pBody.Versions : []))
			.catch(() => []);
	}

	/** The URL of one archived version, laid out on a paper. Never a fresh render. */
	versionURL(pIDBlueprintRevision, pSheet, pReading)
	{
		return this._url('BlueprintRevision/' + (Number(pIDBlueprintRevision) || 0) + '/Sheet')
			+ '?Sheet=' + encodeURIComponent(pSheet || 'Letter')
			+ '&Reading=' + encodeURIComponent(pReading || 'Desk');
	}

	shareState(pSubject)
	{
		if (!this._valid(pSubject)) { return Promise.resolve(null); }
		return libFrameworkClient.getJSON(this.fable, this._url(this._base(pSubject) + '/Share'))
			.catch(() => null);
	}

	shareCreate(pSubject, pBody)
	{
		if (!this._valid(pSubject)) { return Promise.reject(new Error('BlueprintAPI: no blueprint to share.')); }
		return libFrameworkClient.postJSON(this.fable, this._url(this._base(pSubject) + '/Share'), pBody || {});
	}

	shareRevoke(pSubject)
	{
		if (!this._valid(pSubject)) { return Promise.reject(new Error('BlueprintAPI: no blueprint to revoke.')); }
		return libFrameworkClient.delJSON(this.fable, this._url(this._base(pSubject) + '/Share'));
	}

	shareInvite(pSubject, pBody)
	{
		if (!this._valid(pSubject)) { return Promise.reject(new Error('BlueprintAPI: no blueprint to invite to.')); }
		return libFrameworkClient.postJSON(this.fable, this._url(this._base(pSubject) + '/Invite'), pBody || {});
	}

	// ---- printing --------------------------------------------------------------

	/**
	 * Fetch the PDF as a blob. Both dialects have a print route; the by-record one
	 * (GET /1.0/Blueprint/:id/Print) has existed since the print work landed and had no caller in
	 * the client at all, which is the other half of "I have never seen a blueprint PDF".
	 */
	print(pSubject, pSheet)
	{
		if (!this._valid(pSubject)) { return Promise.reject(new Error('BlueprintAPI: no blueprint to print.')); }
		return libFrameworkClient.getBinary(this.fable, this.printURL(pSubject, pSheet));
	}
}

module.exports = PictProviderBlueprintAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
